import { db, storage } from "@/main";
import { firestore } from "firebase";

import {
  DeviceReportFilters,
  getPeriodStart,
} from "@/data/models/DeviceReportFilters";

import {
  DEVICE_REPORT_FILE_EXTENSION,
  DEVICE_REPORTS_COLLECTION,
  DeviceReport,
  getDeviceReportCollectionName,
  parseReportId,
  toStorageObjectName,
  UNASSIGNED_PREFIX,
  UnassignedDeviceReport,
  UnassignedReportKey,
} from "@/data/models/DeviceReport";

/**
 * Reads the device reports the production test rig uploads through the Cloud provisioning API.
 *
 * **Read-only by design.** The API is the only writer and goes through the Admin SDK; the security
 * rules grant `read` to the admin portal and no write at all.
 *
 * Two sources, because a report has a Firestore document **if and only if** it sits under a device
 * document that exists:
 *
 * | Where | Read with |
 * |---|---|
 * | Attached to a device | the `deviceReports` subcollection under that device |
 * | Still waiting | a Cloud Storage listing of `deviceReports/unassigned/**` |
 *
 * ⚠ **Do not add a `collectionGroup("deviceReports")` query with an `orderBy`.** Measured against
 * production on 2026-08-13: it fails at runtime with `FAILED_PRECONDITION`, because a collection
 * group query needs a `COLLECTION_GROUP_DESC` single-field index that does not exist. The
 * per-device queries below need no index at all. A cross-device list is possible, but only after
 * that index is created.
 */

/** Custom Storage metadata the provisioning API writes with every report object. */
const REPORT_METADATA_KEYS = {
  reportType: "iresReportType",
  reportedAt: "iresReportedAt",
  macAddress: "iresMacAddress",
  serialNo: "iresSerialNo",
  overallResult: "iresOverallResult",
  createdBy: "iresCreatedBy",
};

function parseIsoDate(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * One waiting report, from its object metadata.
 *
 * The report id is the fallback for both the timestamp and the type, because it is the report's
 * identity and is present even if a metadata read fails or an object was placed by hand. A failed
 * read degrades the row rather than emptying the list: an operator seeing a report they cannot
 * identify is still better than not knowing it is there.
 */
async function readUnassignedReport(
  key: UnassignedReportKey,
  keyValue: string,
  objectName: string,
  fileName: string
): Promise<UnassignedDeviceReport> {
  const reportId = fileName.endsWith(DEVICE_REPORT_FILE_EXTENSION)
    ? fileName.slice(0, -DEVICE_REPORT_FILE_EXTENSION.length)
    : fileName;
  const fromReportId = parseReportId(reportId);

  const report: UnassignedDeviceReport = {
    id: reportId,
    key,
    keyValue,
    objectName,
    reportType: fromReportId?.reportType ?? "",
    reportedAt: fromReportId?.reportedAt ?? null,
    macAddress: key === UnassignedReportKey.MAC_ADDRESS ? keyValue : null,
    serialNo: key === UnassignedReportKey.SERIAL_NUMBER ? keyValue : null,
    overallResult: null,
    createdBy: null,
    sizeInBytes: 0,
  };

  try {
    const metadata = await storage.ref(objectName).getMetadata();
    const custom: Record<string, string> = metadata.customMetadata ?? {};
    const reportedAt = custom[REPORT_METADATA_KEYS.reportedAt];

    return {
      ...report,
      reportType: custom[REPORT_METADATA_KEYS.reportType] || report.reportType,
      reportedAt: parseIsoDate(reportedAt) ?? report.reportedAt,
      macAddress: custom[REPORT_METADATA_KEYS.macAddress] || report.macAddress,
      serialNo: custom[REPORT_METADATA_KEYS.serialNo] || report.serialNo,
      overallResult: custom[REPORT_METADATA_KEYS.overallResult] || null,
      createdBy: custom[REPORT_METADATA_KEYS.createdBy] || null,
      sizeInBytes: metadata.size ?? 0,
    };
  } catch {
    // Metadata is an optimisation, not the record. Keep what the report id already told us.
    return report;
  }
}

async function listUnassignedReportsFor(
  key: UnassignedReportKey
): Promise<UnassignedDeviceReport[]> {
  const keyFolders = await storage.ref(`${UNASSIGNED_PREFIX}/${key}`).listAll();

  const perKeyValue = await Promise.all(
    keyFolders.prefixes.map(async (keyFolder) => {
      // `items`, not `prefixes`: one report is one object named by its id, so the reports of a
      // serial (or a MAC) are the files directly in its folder.
      const reportObjects = await keyFolder.listAll();

      return Promise.all(
        reportObjects.items.map((reportObject) =>
          readUnassignedReport(key, keyFolder.name, reportObject.fullPath, reportObject.name)
        )
      );
    })
  );

  return perKeyValue.flat();
}

/** Oldest first: the longest-waiting report is the one most likely to need attention. */
function compareReportedAt(
  first: UnassignedDeviceReport,
  second: UnassignedDeviceReport
): number {
  // A report with no readable timestamp sorts last rather than jumping to the top of the queue.
  if (!first.reportedAt || !second.reportedAt) {
    return Number(Boolean(second.reportedAt)) - Number(Boolean(first.reportedAt));
  }

  return first.reportedAt.getTime() - second.reportedAt.getTime();
}

/**
 * Every report stored under one device, newest first.
 *
 * Ordered by `reportedAt` (when the device was tested) rather than `uploadedAt`, because a report
 * claimed weeks after its test would otherwise sort as if it were new.
 */
export async function getDeviceReports(serialNo: string): Promise<DeviceReport[]> {
  const snapshot = await db
    .collection(getDeviceReportCollectionName(serialNo))
    .doc(serialNo)
    .collection(DEVICE_REPORTS_COLLECTION)
    .orderBy("reportedAt", "desc")
    .get();

  return snapshot.docs.map((document) => ({
    ...(document.data() as DeviceReport),
    id: document.id,
  }));
}

/**
 * Every report still waiting to be attached to a device, oldest first.
 *
 * Built entirely from Cloud Storage, since these reports have no Firestore document. The layout is
 * `unassigned/{key}/{keyValue}/{reportId}.json`, and Storage listings are one level at a time, so
 * this walks two levels and then reads each report's metadata. That is one request per waiting
 * report; the queue is small by nature (a report leaves it as soon as its device is created), and it
 * is the price of the reports being visible before their device exists at all.
 */
export async function getUnassignedDeviceReports(): Promise<UnassignedDeviceReport[]> {
  const perKey = await Promise.all([
    listUnassignedReportsFor(UnassignedReportKey.SERIAL_NUMBER),
    listUnassignedReportsFor(UnassignedReportKey.MAC_ADDRESS),
  ]);

  return perKey
    .flat()
    .sort((first, second) => compareReportedAt(first, second));
}

/**
 * The report payload itself.
 *
 * Fetched through a download URL rather than `getBytes`, which the pinned `firebase@7` client SDK
 * does not have. Returns the parsed JSON untyped on purpose: the rig's payload shape changes with
 * every hardware revision, so the viewer walks it rather than binding fixed fields.
 *
 * @param location A `storageLocation` (leading slash) or a plain object name.
 */
export async function getDeviceReportContent(location: string): Promise<unknown> {
  const url = await storage.ref(toStorageObjectName(location)).getDownloadURL();
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Could not download the report (HTTP ${response.status})`);
  }

  return response.json();
}

/**
 * How many indexed reports one page loads.
 *
 * Bounded because the count on the filter screen is honest about being a page: past this the screen
 * says "ca. N", exactly as the RENO queue does, rather than pretending to a total it did not count.
 */
export const DEVICE_REPORT_PAGE_SIZE = 200;

/**
 * Reports attached to devices, newest first, narrowed by the filters Firestore can answer.
 *
 * The period becomes a range on `reportedAt`; everything else is applied by the caller against the
 * loaded page. That split is what keeps this to **one** index: the collection-group exemption on
 * `reportedAt` created 2026-08-14. Adding an equality filter here (category, type, result) would
 * need its own composite index with collection-group scope, and would fail at runtime with
 * FAILED_PRECONDITION until it existed.
 *
 * A serial narrows to that device's subcollection instead, which needs no collection-group index at
 * all and returns every report for the device rather than a page.
 */
export async function queryAssignedDeviceReports(
  filters: DeviceReportFilters,
  now: Date
): Promise<{ reports: DeviceReport[]; isPage: boolean }> {
  if (filters.serialNo) {
    return { reports: await getDeviceReports(filters.serialNo), isPage: false };
  }

  const periodStart = getPeriodStart(filters, now);
  let query = db
    .collectionGroup(DEVICE_REPORTS_COLLECTION)
    .orderBy("reportedAt", "desc") as firestore.Query;

  if (periodStart) {
    query = query
      .where("reportedAt", ">=", firestore.Timestamp.fromDate(periodStart))
      .orderBy("reportedAt", "desc");
  }

  const snapshot = await query.limit(DEVICE_REPORT_PAGE_SIZE).get();

  return {
    reports: snapshot.docs.map((document) => ({
      ...(document.data() as DeviceReport),
      id: document.id,
    })),
    // A full page may have more behind it; a short one cannot.
    isPage: snapshot.size === DEVICE_REPORT_PAGE_SIZE,
  };
}
